/**
 * Home Feature - English (en-US)
 */

import type { HomeLocale } from './types';

const enUS: HomeLocale = {
    home: {
        title: 'Accelerate Patent Drafting with AI',
        subtitle: 'Intelligently complete the full patent documentation process from invention disclosures to office action responses',

        tabs: {
            patent: 'Patent Application',
            disclosure: 'Invention Disclosure',
            oar: 'Office Action Response',
        },

        cardTitles: {
            patent: 'Patent Application',
            disclosure: 'Invention Disclosure',
            oar: 'Office Action Response',
        },

        placeholders: {
            patent: 'Describe your technical solution, the problem it addresses, and its innovative aspects.\n\nYou can also type / to use a template or upload files.',
            disclosure: 'Describe your invention idea, the technical problem it solves, and key innovations.\n\nYou can also type / to use a template or upload files.',
            oar: 'Paste the office action notice content here, or describe the rejection reasons you need to address.',
        },

        placeholder: {
            default: 'Describe your invention or type / to use a template',
            template: 'Type / to browse patent templates',
            example1: 'Try: A machine learning system for image recognition...',
            example2: 'Try: An improved battery design with higher capacity...',
            example3: 'Example: / USPTO template for software patents',
        },

        workMode: {
            agent: 'Agent',
            copilot: 'Copilot',
        },

        actions: {
            addFiles: 'Add Files',
            addFilesTooltip: 'Upload files (PDF, Word, TXT)',
            readOnlyTip: 'You have read-only permission and cannot upload files',
            dropToAdd: 'Drop to add files',
            style: 'Style',
            styleTooltip: 'Choose drafting style',
            template: 'Template',
            templateTooltip: 'Choose patent template',
            voiceInput: 'Voice Input',
            voiceInputTooltip: 'Voice input',
            voiceInputSoon: 'Voice input coming soon...',
            generate: 'Start',
            loggingIn: 'Logging in...',
            startDrafting: 'Start Drafting',
            draftingStyle: 'Drafting Style',
            selectTemplate: 'Select Template',
            removeFile: 'Remove',
            loginNow: 'Log In',
            changeTemplate: 'Click to change template',
        },

        upload: {
            dragOrClick: 'Drag files here or click to upload',
            supportedFormats: 'Supports PDF, Word, TXT, and image files',
        },

        messages: {
            filesAdded: 'Added {count} file(s)',
            pleaseEnterContent: 'Please enter content or upload files',
            pleaseUploadFile: 'Please upload office action file',
            generating: 'Generating patent document...',
            navigationFailed: 'Navigation failed',
            styleApplied: 'Applied style: {name}',
            templateApplied: 'Template selected: {name}',
            mentionApplied: 'Selected {type}: {name}',
            templateLoading: 'Loading template: {name}',
            submitFailed: 'Submit failed, please try again',
            invalidFile: 'Invalid file',
            uploadFailed: 'File upload failed, please try again',
            filesUploading: 'Files are uploading, please wait',
            fileStatusUploading: 'Uploading...',
            fileStatusFailed: 'Upload failed',
            someFilesNotUploaded: 'Some files failed to upload, please upload again',
            emptyInput: 'Please enter content or upload files',
            emptyOarInput: 'Please enter patent application number or upload files',
            fileProcessed: 'File {name} processed successfully',
            fileProcessFailed: 'File {name} processing failed',
            fileProcessingTimeout: 'File processing timeout, please re-upload',
            filesProcessing: 'Files are being processed, please wait...',
            filesNotReady: 'Some files are still being processed, please wait before creating task',
            fileAlreadyExists: 'File {name} already exists, using existing file',
            defaultPromptForPatent: 'Please parse the uploaded documents, generate technical disclosure analysis report and patent application documents.',
            defaultPromptForDisclosure: 'Process the files following the standard invention disclosure analysis workflow to generate an invention disclosure',
            loginRequired: 'Please log in to start drafting. Your work will be saved to your account.',
        },

        // Unified initial prompts configuration (automatically selected based on task type and scenario)
        initialPrompts: {
            patent_draft: {
                withFiles: 'Please parse the uploaded documents, generate technical disclosure analysis report and patent application documents.',
                empty: 'Please start drafting the patent application',
            },
            disclosure: {
                withFiles: 'Process the files following the standard invention disclosure analysis workflow to generate an invention disclosure',
                empty: 'Please start drafting the invention disclosure',
            },
            prosecution: {
                withPatentNumber: 'Start analyzing office action (Application No.: {patentNumber})',
                withFiles: 'Start analyzing office action',
                empty: 'Please analyze the office action and generate a response',
            },
        },

        mention: {
            title: 'Templates',
            selectType: 'Select Type',
            styleTitle: 'Style',
            priorArt: 'Prior Art',
            file: 'File',
            document: 'Document',
            loading: 'Loading...',
            navigate: 'Navigate',
            select: 'Select',
            close: 'Close',
            noResults: 'No templates found for "{query}"',
            tryDifferent: 'Try a different search term',
            applicationDate: 'Application:',
            publicationDate: 'Publication:',
            docTypes: {
                patent_draft: 'Patent Drafting',
                disclosure: 'Technical Disclosure',
                prosecution: 'Office Action Response',
            },
            docStatus: {
                draft: 'Draft',
                in_review: 'In Review',
                approved: 'Approved',
                filed: 'Filed',
            },
            types: {
                template: {
                    label: 'Template',
                    desc: 'Patent application template',
                },
                style: {
                    label: 'Writing Style',
                    desc: 'Writing style settings',
                },
                priorArt: {
                    label: 'Prior Art',
                    desc: 'Prior art patent',
                },
                file: {
                    label: 'File',
                    desc: 'Attachment file',
                },
                document: {
                    label: 'Document',
                    desc: 'Project document',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO Utility Patent',
                usptoSoftwarePatent: 'USPTO Software Patent',
                epoPatentApplication: 'EPO Patent Application',
                pctInternationalApplication: 'PCT International Application',
                cnipaPatentApplication: 'CNIPA Patent Application',
            },
        },

        tooltips: {
            uploadFile: 'Upload file',
            voiceInput: 'Voice input',
        },

        patent: {
            jurisdiction: 'Please select application region',
        },

        oar: {
            title: 'Office Action Response',
            projectTitle: 'Office Action Response - {patentNumber}',
            projectTitleWithoutNumber: 'Office Action Response',
            initialPrompt: 'Start analyzing office action',
            initialPromptWithNumber: 'Start analyzing office action (Application No.: {patentNumber})',
            confirmMessage: 'OAR analysis results confirmed, please continue processing',
            oarExtractList: 'OAR Extract List',
            rejectionTableTitle: 'Rejection Analysis Table',
            rejectionAnalysis: {
                title: 'Rejection Analysis',
                status: {
                    extracting: 'Extracting data',
                    processing: 'Processing',
                },
                button: {
                    confirm: 'Confirm and Continue',
                    saving: 'Saving',
                    addClause: 'Add Clause',
                    showMore: 'Show more',
                    showLess: 'Show less',
                    export: 'Export Excel',
                    exportSheetName: 'Rejection Analysis',
                    viewDetail: 'View the Logical Chain',
                    delete: 'Delete',
                },
                detail: {
                    drawerTitle: 'Analysis Detail',
                    examinerOpinion: 'Examiner Opinion',
                    featureComparison: 'Feature Comparison',
                    claimFeature: 'Claim Feature',
                    priorArtFeature: 'Prior Art Feature',
                    priorArtLabel: 'Prior Art {no} · {name}',
                    noPriorArt: 'No prior art',
                    strategy: 'Response Strategy',
                    strategyReason: 'Strategy Basis',
                    prevItem: 'Previous',
                    nextItem: 'Next',
                },
                direction: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                basicInfo: {
                    title: 'Basic Info',
                    application: 'Application No.',
                    oaNo: 'OA No.',
                    type: 'Type',
                    examiner: 'Examiner',
                    issueDate: 'Issue Date',
                    priorArt: 'Prior Art',
                    applicationFiles: 'Application Files',
                },
                tabs: {
                    all: 'All',
                },
                tableHeaders: {
                    claim: 'Claim',
                    statute: 'Statute',
                    claimAndStatute: 'Claim/Statute',
                    claimElement: 'Claim Element',
                    examinerReasoning: 'Examiner Reasoning',
                    aiAnalysis: 'AI Analysis',
                    strategy: 'Response Strategy',
                },
                statusPills: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                labels: {
                    direction: 'Response Direction',
                    strategy: 'Response Strategy',
                    claimAmendment: 'Claim Amendment',
                    amendmentMethod: 'Amendment Method',
                    amendmentBasis: 'Amendment Basis',
                },
                needAmendment: {
                    yes: 'Amend',
                    no: 'No Amend',
                    optional: 'AI Decide',
                },
                tooltips: {
                    direction: 'Whether to agree with the examiner\'s opinion',
                    strategy: 'Response strategy to the rejection',
                    claimAmendment: 'Whether claims need amendment',
                },
                placeholder: {
                    edit: 'Click to edit...',
                    claimElement: 'Enter claim element...',
                    examinerReasoning: 'Enter examiner reasoning...',
                    strategy: 'Enter response strategy...',
                    amendmentMethod: 'Enter amendment method...',
                    amendmentBasis: 'Enter amendment basis...',
                },
                emptyState: {
                    noData: 'No rejection data',
                    hint: 'Rejection analysis will be displayed here after extraction',
                },
                exportHeaders: {
                    claim: 'Claim',
                    statute: 'Statute',
                    claimElement: 'Claim Element',
                    examinerReasoning: 'Examiner Reasoning',
                    direction: 'Response Direction',
                    strategy: 'Response Strategy',
                    distinguishingFeatures: 'Distinguishing Features',
                    priorArt: 'Prior Art',
                    needAmendment: 'Need Amendment',
                    amendmentMethod: 'Amendment Method',
                    amendmentBasis: 'Amendment Basis',
                },
                message: {
                    noDataId: 'Unable to get data ID',
                    noData: 'No data available',
                    saveSuccess: 'Saved successfully',
                    saveFailed: 'Save failed',
                    deleteSuccess: 'Deleted successfully',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
            },
            patentNumber: {
                label: 'Patent Application Number',
                placeholder: 'Enter the Application Number of the Patent under examination to retrieve the latest office action',
            },
            uploadAction: {
                title: 'Upload Office Action',
                choose: 'Choose File',
                description: 'Upload the PDF/Word file of the Office Action/Opinion that requires a response',
                uploading: 'Uploading',
                addMore: 'Continue uploading more files',
            },
            uploadedFiles: {
                title: 'Uploaded Files ({count})',
            },
            fileStatus: {
                preparing: 'Preparing file...',
                analyzing: 'Analyzing content...',
                finalizing: 'Almost done...',
                processing: 'Processing...',
                estimatedTime: 'Estimated 1-2 minutes',
                completed: 'Completed',
                failed: 'Parsing failed',
            },
            actions: {
                agent: 'Agent',
                copilot: 'Copilot',
                start: 'Start',
            },
            search: {
                failed: 'Patent search failed, please try again',
                notFound: 'Patent not found',
            },
            patent: {
                publicationNumber: 'Publication Number:',
                publicationDate: 'Publication Date:',
                currentApplicant: '[Current] Applicant:',
            },
            download: {
                searching: 'Searching for office action PDF...',
                downloading: 'PDF download started, please check browser downloads',
                success: 'PDF downloaded successfully',
                failed: 'PDF download failed, please try again',
                notFound: 'Office action PDF not found, you can manually upload',
                visitUsptoWebsite: 'Visit USPTO website to download',
            },
            unsupportedRegion: 'This region is not yet supported',
            prosecutionTip: 'Enter the Patent Application Number / Upload the Office Action/Opinion PDF to get started',
            rejectionDetail: {
                drawerTitle: 'Rejection Detail',
                examinerOpinion: 'Examiner Opinion',
                featureComparison: 'Feature Comparison',
                claimFeature: 'Claim Feature',
                specificationSource: 'Specification Source',
                priorArtFeature: 'Prior Art Feature',
                priorArtLabel: 'Prior Art {no} ({name})',
                noPriorArt: 'No prior art information available',
                priorArtSource: '{name} Source',
                distinguishingFeatures: 'Distinguishing Features',
                aiStrategy: 'AI Response Strategy',
                strategyReason: 'Strategy Basis',
                crossValidation: 'Cross Validation',
                prevItem: 'Previous',
                nextItem: 'Next',
                direction: {
                    agree: 'Agree',
                    unsure: 'Unsure',
                    disagree: 'Disagree',
                },
                claimAmendment: {
                    title: 'Claim Amendment Suggestion',
                    needAmendment: 'Need Amendment',
                    yes: 'Amend',
                    no: 'No Amend',
                    optional: 'AI Decide',
                    method: 'Amendment Method',
                    basis: 'Amendment Basis',
                },
            },
        },

        draftingStyle: {
            title: 'Select Drafting Style',
            stylesHeader: 'Styles',
            previewHeader: 'Preview',
            cancel: 'Cancel',
            apply: 'Apply Style',
            loading: 'Loading...',
            loadingPreview: 'Loading preview...',
            loadError: 'Failed to load styles',
            loadDetailError: 'Failed to load style detail',
            noStyles: 'No styles available',
            noPreview: 'No preview available',
            pleaseSelect: 'Please select a style first',
            used: 'Used {count} times',
            styles: {
                standard: {
                    name: 'Standard Format',
                    description: 'Traditional patent format with numbered paragraphs and formal language',
                },
                detailed: {
                    name: 'Detailed Technical',
                    description: 'Comprehensive technical descriptions with extensive embodiment details',
                },
                concise: {
                    name: 'Concise Claims-Focused',
                    description: 'Streamlined format emphasizing clear and strong claim language',
                },
                international: {
                    name: 'PCT International',
                    description: 'Format optimized for international patent applications under PCT',
                },
                continuation: {
                    name: 'Continuation Application',
                    description: 'Format for continuation or divisional applications referencing parent applications',
                },
            },
        },

        templateSelection: {
            title: 'Select Template',
            templatesHeader: 'Templates',
            previewHeader: 'Preview',
            cancel: 'Cancel',
            apply: 'Apply Template',
            loading: 'Loading...',
            loadingPreview: 'Loading preview...',
            loadError: 'Failed to load templates',
            loadDetailError: 'Failed to load template detail',
            noTemplates: 'No templates available',
            noPreview: 'No preview available',
            pleaseSelect: 'Please select a template first',
        },

        documentModifications: {
            title: 'Document Modifications Applied',
            summary: '{count} modifications in total',
            streamingSummary: 'Generating edits, {count} detected',
            streamingTitle: 'AI is editing the document',
            streamingDesc: 'Receiving modification content. The backend will merge it and refresh the latest version when complete.',
            streamPreview: 'Live modification stream',
            appliedSummary: '{count} modifications applied',
            appliedTitle: 'Modifications applied automatically',
            appliedDesc: 'The backend has merged the edits and created a new version. The editor refreshes to the latest content.',
            emptyState: 'No modification details',
            noContent: 'No new content',
            oldContent: 'Old text:',
            actions: {
                insert: 'Insert',
                replace: 'Replace',
                delete: 'Delete',
            },
            tableHeaders: {
                index: '#',
                documentId: 'Document ID',
                action: 'Action',
                dataId: 'data-id',
                content: 'Modified Content',
                oldContent: 'Old Text',
            },
            exportExcel: 'Export Excel',
        },
    },
};

export default enUS;
